import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { Dtos, FileType, formatToDollar } from "@inrev/common";
import { formatEmpty, formatFromISODate } from "../../../../utils";
import { cn } from "../../../lib/utils";
import { Card, ClickableIcon, Icon, Text } from "../../../primitives";
import { LineItem } from "../../../ui/LineItem";
import { FileUpload } from "../../../ui/Upload";

type WIPStatementCardProps = {
	wipStatement: Dtos.WipStatement.Get.Response;
	partialActive: boolean;
	modelSize?: "small" | "medium" | "large";
	disabled?: boolean;
	readOnly?: boolean;
	alwaysExpanded?: boolean;
	onEditClick?: () => void;
	onFilesChange?: (files: Dtos.WipStatement.Get.Response["files"]) => void;
};

export const WIPStatementCard = ({
	wipStatement,
	partialActive,
	modelSize,
	disabled,
	readOnly,
	alwaysExpanded = false,
	onEditClick,
	onFilesChange,
}: WIPStatementCardProps) => {
	const showFileUpload = !readOnly || wipStatement.files.length > 0;
	const dateLabel = formatFromISODate(wipStatement.date);

	const editControl = onEditClick ? (
		<ClickableIcon
			type="pencil-square"
			size="mini"
			color="secondary"
			aria-label="Edit work in progress statement"
			onClick={onEditClick}
		/>
	) : null;

	const details = (
		<>
			<div className="space-y-3">
				<LineItem
					label="Backlog (CTC)"
					partial={wipStatement.projectBacklogCost == null}
					partialActive={partialActive}
				>
					{formatEmpty(formatToDollar(wipStatement.projectBacklogCost))}
				</LineItem>
				<LineItem
					label="Gross Profit In Backlog"
					partial={modelSize === "large" && wipStatement.projectBacklogGrossProfit == null}
					partialActive={partialActive}
				>
					{formatEmpty(formatToDollar(wipStatement.projectBacklogGrossProfit))}
				</LineItem>
			</div>
			{showFileUpload && (
				<FileUpload
					value={wipStatement.files}
					onChange={(files) => onFilesChange?.(files)}
					enableUpload={!readOnly && !wipStatement.underwriterLock}
					enableTypeSelection={false}
					showTabs={false}
					allowDelete={!readOnly && !wipStatement.underwriterLock}
					expandedByDefault={true}
					allowedTypesAndLabels={{
						[FileType.wip]: "Work In Progress Schedule",
					}}
					maxTypesPerFile={2}
					analyticsContext="wip-statements"
					trackingPrefix="account-wip-statement-card-upload"
				/>
			)}
		</>
	);

	return (
		<Card
			elevation="raised"
			className={cn("rounded-xl", disabled && "pointer-events-none opacity-50")}
			data-testid="agent-account-wip-statement-card"
		>
			{alwaysExpanded ? (
				<Card.Body className="flex w-full flex-col space-y-5 p-6">
					<div className="flex items-center justify-between w-full gap-2">
						<Text type="title" color="primary" className="flex-1">
							{dateLabel}
						</Text>
						{editControl}
					</div>
					{details}
				</Card.Body>
			) : (
				<Disclosure>
					{({ open }) => (
						<Card.Body className="p-6">
							<div className="flex items-center w-full gap-2">
								<DisclosureButton className="flex min-w-0 flex-1 items-center text-left cursor-pointer">
									<Text type="title" color="primary">
										{dateLabel}
									</Text>
								</DisclosureButton>
								{editControl}
								<DisclosureButton
									data-testid="agent-account-wip-statement-toggle"
									className="shrink-0 cursor-pointer"
								>
									<Icon
										type="chevron-right"
										size="mini"
										color="tertiary"
										className={cn("transition-transform duration-150", open && "rotate-90")}
									/>
								</DisclosureButton>
							</div>
							<DisclosurePanel>
								<div className="mt-5 flex flex-col space-y-5">{details}</div>
							</DisclosurePanel>
						</Card.Body>
					)}
				</Disclosure>
			)}
		</Card>
	);
};
